import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import Layout, { TopBar } from '../components/Layout';
import { Button, Meta, Display, Rule } from '../components/Atoms';

/**
 * Export - eksport danych użytkownika.
 *
 * Endpointy:
 *   GET /api/export/plots/json
 *   GET /api/export/plots/csv
 *   GET /api/export/sprays/json
 *   GET /api/export/sprays/csv
 *   GET /api/export/complete/json - kompletny backup
 *   GET /api/export/ical - kalendarz wszystko
 *   GET /api/export/tasks/ical - kalendarz zadań
 */

function ExportCard({ title, description, formats, onDownload, busy }) {
  return (
    <div style={{
      padding: '24px 26px', borderRadius: 14,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 18,
    }}>
      <Display size={20} style={{ display: 'block', marginBottom: 6 }}>{title}</Display>
      <div style={{
        fontSize: 13, color: 'var(--t-text-dim)',
        lineHeight: 1.5, marginBottom: 18,
      }}>{description}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {formats.map(f => (
          <Button
            key={f.key}
            variant={f.primary ? 'primary' : 'ghost'}
            onClick={() => onDownload(f.key, f.endpoint, f.filename)}
            disabled={busy}
          >{f.label}</Button>
        ))}
      </div>
    </div>
  );
}

export default function Export() {
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [feedUrl, setFeedUrl] = useState(null);
  const BASE = window.location.origin;

  const handleEnable = async () => {
    setBusy('enable');
    try {
      const res = await axios.post('/api/calendar/feed/enable');
      setFeedUrl(res.data.webcalUrl || `${BASE}${res.data.url}`);
      setMessage(null);
    } catch (err) {
      setMessage({ type: 'err', text: 'Nie udało się włączyć subskrypcji.' });
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async () => {
    setBusy('regenerate');
    try {
      const res = await axios.post('/api/calendar/feed/enable');
      setFeedUrl(res.data.webcalUrl || `${BASE}${res.data.url}`);
      setMessage({ type: 'ok', text: 'Nowy link wygenerowany — poprzedni przestał działać.' });
    } catch (err) {
      setMessage({ type: 'err', text: 'Nie udało się wygenerować nowego linku. Spróbuj ponownie.' });
    } finally {
      setBusy(null);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleDownload = async (key, endpoint, filename) => {
    setBusy(key);
    setMessage(null);
    try {
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'ok', text: `Pobrano: ${filename}` });
    } catch (err) {
      console.error('Export error', err);
      setMessage({ type: 'err', text: 'Nie udało się pobrać pliku.' });
    } finally {
      setBusy(null);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const stamp = new Date().toISOString().split('T')[0];

  return (
    <Layout topBar={<TopBar left={<Meta>Eksport danych</Meta>} />}>
      <div style={{
        maxWidth: 760, margin: '0 auto',
        padding: '40px 32px 60px',
      }}>
        <Meta color="sage" style={{ marginBottom: 10 }}>Eksport</Meta>
        <Display size={'clamp(36px, 6vw, 48px)'} style={{
          display: 'block', letterSpacing: '-.03em', lineHeight: 1,
          marginBottom: 14,
        }}>Twoje dane do pobrania</Display>
        <div style={{
          fontSize: 15, color: 'var(--t-text-dim)',
          lineHeight: 1.55, marginBottom: 36, maxWidth: 540,
        }}>
          Wszystko co zarejestrowałeś — działki, grządki, zbiory, opryski, zadania — możesz pobrać w dowolnym momencie. Dane są Twoje.
        </div>

        {message && (
          <div style={{
            padding: '12px 16px', borderRadius: 10,
            background: message.type === 'ok'
              ? 'rgba(168, 196, 158, 0.10)'
              : 'rgba(192, 106, 74, 0.08)',
            borderLeft: `3px solid var(--t-${message.type === 'ok' ? 'sage' : 'rust'})`,
            marginBottom: 24, fontSize: 13, color: 'var(--t-text)',
          }}>{message.text}</div>
        )}

        <Rule label="I — działki i grządki" />
        <ExportCard
          title="Mój ogród"
          description="Lista działek, grządek, posadzonych roślin, zbiorów. Format JSON do dalszej analizy lub CSV do otwarcia w arkuszu."
          busy={busy === 'plots-json' || busy === 'plots-csv'}
          formats={[
            { key: 'plots-json', label: 'JSON',  endpoint: '/api/export/plots/json', filename: `garden-plots-${stamp}.json`, primary: true },
            { key: 'plots-csv',  label: 'CSV',   endpoint: '/api/export/plots/csv',  filename: `garden-plots-${stamp}.csv` },
          ]}
          onDownload={handleDownload}
        />

        <Rule label="II — opryski" />
        <ExportCard
          title="Historia ochrony roślin"
          description="Wszystkie opryski razem z datami, dawkami, karencjami i bezpiecznymi terminami zbiorów. Przydatne do dziennika ogrodnika lub pokazania komuś."
          busy={busy === 'sprays-json' || busy === 'sprays-csv'}
          formats={[
            { key: 'sprays-json', label: 'JSON', endpoint: '/api/export/sprays/json', filename: `garden-sprays-${stamp}.json`, primary: true },
            { key: 'sprays-csv',  label: 'CSV',  endpoint: '/api/export/sprays/csv',  filename: `garden-sprays-${stamp}.csv` },
          ]}
          onDownload={handleDownload}
        />

        <Rule label="III — kalendarz" />
        <ExportCard
          title="Kalendarz iCal"
          description="Wszystkie nadchodzące zadania jako wydarzenia kalendarza. Otwórz w Google Calendar, Apple Calendar lub innej apce — będziesz dostawać przypomnienia."
          busy={busy === 'tasks-ical' || busy === 'all-ical'}
          formats={[
            { key: 'tasks-ical', label: 'Tylko zadania', endpoint: '/api/export/tasks/ical', filename: `garden-tasks-${stamp}.ics`, primary: true },
            { key: 'all-ical',   label: 'Wszystko',      endpoint: '/api/export/ical',       filename: `garden-${stamp}.ics` },
          ]}
          onDownload={handleDownload}
        />

        <Rule label="IV — subskrypcja kalendarza" />
        <div style={{
          padding: '24px 26px', borderRadius: 14,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          marginBottom: 18,
        }}>
          <Display size={20} style={{ display: 'block', marginBottom: 6 }}>
            Subskrybuj w kalendarzu
          </Display>
          <div style={{
            fontSize: 13, color: 'var(--t-text-dim)',
            lineHeight: 1.5, marginBottom: 18,
          }}>
            Żywy feed zadań i karencji do Twojego kalendarza (Google Calendar, Apple Calendar).
            Zmiany w aplikacji pojawiają się automatycznie po odświeżeniu kalendarza.
            Każdy kalendarz ma unikalny, prywatny token — udostępniaj go ostrożnie.
          </div>

          {feedUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{
                padding: '12px 14px', borderRadius: 8,
                background: 'var(--t-bg)',
                border: '1px solid var(--t-line-2)',
                fontFamily: 'var(--t-mono)', fontSize: 12,
                color: 'var(--t-text-dim)',
                wordBreak: 'break-all',
              }}>{feedUrl}</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(feedUrl);
                      setMessage({ type: 'ok', text: 'URL skopiowany!' });
                    } catch {
                      // fallback
                      const input = document.createElement('input');
                      input.value = feedUrl;
                      document.body.appendChild(input);
                      input.select();
                      document.execCommand('copy');
                      document.body.removeChild(input);
                      setMessage({ type: 'ok', text: 'URL skopiowany!' });
                    }
                  }}
                >📋 Kopiuj URL</Button>
                <Button
                  variant="ghost"
                  onClick={handleRegenerate}
                  disabled={busy === 'regenerate'}
                >{busy === 'regenerate' ? '…' : '↻ Wygeneruj nowy link'}</Button>
                <Button variant="ghost" onClick={() => setFeedUrl(null)}>Ukryj</Button>
              </div>
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={handleEnable}
              disabled={busy === 'enable'}
            >{busy === 'enable' ? '…' : '🔗 Włącz subskrypcję'}</Button>
          )}
        </div>

        <Rule label="V — backup pełny" />
        <ExportCard
          title="Kompletna kopia"
          description="Wszystko, co aplikacja wie o Twoim ogrodzie, w jednym pliku JSON. Trzymaj jako backup albo przenoś między kontami."
          busy={busy === 'complete'}
          formats={[
            { key: 'complete', label: 'Pobierz backup', endpoint: '/api/export/complete/json', filename: `garden-backup-${stamp}.json`, primary: true },
          ]}
          onDownload={handleDownload}
        />

        <div style={{
          marginTop: 50, paddingTop: 24,
          borderTop: '1px solid var(--t-line)',
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.16em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)', textAlign: 'center',
        }}>
          Wszystkie eksporty są generowane na żądanie · pobranie nie usuwa danych z konta
        </div>
      </div>
    </Layout>
  );
}
